import { useTradeFairQuery } from "@/lib/queries";
import { Calendar, MapPin, Sparkles, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useSettings, buildWhatsAppLink } from "@/context/SettingsContext";

export default function TradeFair() {
  const { settings } = useSettings();
  const { data: tf } = useTradeFairQuery();

  if (!tf) return null;

  const ctaHref = tf.cta_link?.trim()
    ? tf.cta_link
    : buildWhatsAppLink(settings, "I'd like to attend the KEMZ Trade Fair 2026 event.");
  const isExternalLink = tf.cta_link?.trim();

  return (
    <section className="relative py-24 md:py-32 bg-[#0A0A0A]" data-testid="tradefair-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden gold-border bg-[#0F0F0F]"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#D4AF37]/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#D4AF37]/8 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-10 p-8 md:p-14">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37]/50 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="text-[0.65rem] tracking-[0.28em] uppercase text-[#D4AF37]" data-testid="tradefair-eyebrow">
                  {tf.eyebrow || "Exclusive Event"}
                </span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white leading-tight" data-testid="tradefair-title">
                {tf.title}
              </h2>

              <p className="text-white/65 mt-5 text-lg max-w-xl" data-testid="tradefair-description">
                {tf.description}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={ctaHref}
                  target={isExternalLink ? "_blank" : "_blank"}
                  rel="noreferrer"
                  className={isExternalLink ? "btn-primary" : "btn-whatsapp"}
                  data-testid="tradefair-cta-button"
                >
                  {!isExternalLink && <MessageCircle className="w-5 h-5" />}
                  {tf.cta_text || "Save the Date"}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col gap-4">
              {tf.image ? (
                <div className="relative aspect-[4/3] border border-[#D4AF37]/30 overflow-hidden" data-testid="tradefair-image">
                  <img src={tf.image} alt={tf.title} className="absolute inset-0 w-full h-full object-cover" />
                </div>
              ) : null}
              <div className="border border-[#D4AF37]/30 bg-[#0A0A0A]/60 backdrop-blur-sm p-6">
                <div className="flex items-center gap-3 text-[#D4AF37]">
                  <Calendar className="w-5 h-5" />
                  <span className="text-[0.65rem] uppercase tracking-[0.28em]">Date</span>
                </div>
                <div className="font-serif text-2xl text-white mt-2" data-testid="tradefair-date">{tf.date}</div>
              </div>
              <div className="border border-[#D4AF37]/30 bg-[#0A0A0A]/60 backdrop-blur-sm p-6">
                <div className="flex items-center gap-3 text-[#D4AF37]">
                  <MapPin className="w-5 h-5" />
                  <span className="text-[0.65rem] uppercase tracking-[0.28em]">Venue</span>
                </div>
                <div className="font-serif text-xl text-white mt-2" data-testid="tradefair-venue">{tf.venue}</div>
                <div className="text-sm text-white/55 mt-1" data-testid="tradefair-stand">{tf.stand}</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
